<template>
  <div
    class="ecmt-certificate"
    :class="'lang-' + lang"
  >
    <div class="ecmt-cert-no">
      {{ t.certificateNoLabel }} {{ certificateNoDisplay }}
    </div>

    <div class="ecmt-title">
      {{ t.title }}
    </div>

    <table class="ecmt-info-table">
      <colgroup>
        <col class="ecmt-info-label-col">
        <col class="ecmt-info-value-col">
      </colgroup>
      <tbody>
        <tr>
          <td class="label">
            {{ t.registrationNumber }}
          </td>
          <td class="value">
            {{ invoiceData.CarRegNo || '' }}
          </td>
        </tr>
        <tr>
          <td class="label">
            {{ t.complianceNumber }}
          </td>
          <td class="value">
            {{ invoiceData.documentNumber || '' }}
          </td>
        </tr>
        <tr>
          <td class="label">
            {{ t.vehicleTypeMake }}
          </td>
          <td class="value">
            {{ vehicleTypeMake }}
          </td>
        </tr>
        <tr>
          <td class="label">
            {{ t.vin }}
          </td>
          <td class="value">
            {{ invoiceData.VIN || '' }}
          </td>
        </tr>
        <tr v-if="!isTrailerGroup">
          <td class="label">
            {{ t.engineTypeNumber }}
          </td>
          <td class="value">
            {{ engineTypeNumber }}
          </td>
        </tr>
      </tbody>
    </table>

    <div class="ecmt-the">
      {{ t.the }}
    </div>

    <div class="ecmt-company-block">
      <div class="ecmt-company-name">
        {{ companyName }}
      </div>
      <div
        v-if="companyAddress"
        class="ecmt-company-address"
      >
        {{ companyAddress }}
      </div>
    </div>

    <p class="ecmt-legal">
      {{ t.legalIntro }}
    </p>
    <p class="ecmt-legal-confirm">
      {{ t.legalConfirm }}
    </p>

    <ul class="ecmt-checklist">
      <li
        v-for="item in checklistItems"
        :key="item.id"
        :class="{ 'is-child': item.isChild }"
      >
        <span
          class="ecmt-checkbox"
          :class="{ checked: item.checked }"
        >
          <span v-if="item.checked">✓</span>
        </span>
        <span class="ecmt-check-label">
          {{ item.label }}<sup v-if="item.note">{{ item.note }}</sup>
        </span>
      </li>
    </ul>

    <table class="ecmt-sign-table">
      <tbody>
        <tr class="ecmt-sign-values-row">
          <td>{{ place }}</td>
          <td>{{ testDate }}</td>
          <td />
        </tr>
        <tr class="ecmt-sign-line-row">
          <td colspan="3">
            <div class="ecmt-sign-full-line" /></td>
        </tr>
        <tr class="ecmt-sign-labels-row">
          <td>{{ t.place }}</td>
          <td>{{ t.date }}</td>
          <td>{{ t.signature }}</td>
        </tr>
      </tbody>
    </table>

    <div class="ecmt-next-note">
      <template v-if="isHy">
        <strong class="ecmt-note-lead">Նշում.</strong>
        Հաջորդ ճանապարհային պիտանելիության փորձարկումը պահանջվում է մինչև<sup>6</sup>՝
      </template>
      <template v-else>
        <strong class="ecmt-note-lead">Note:</strong>
        Next roadworthiness test required by<sup>6</sup>:
      </template>
      <span class="ecmt-next-date">{{ nextTestDate }}</span>
    </div>

    <hr class="ecmt-footnotes-line">

    <div class="ecmt-footnotes">
      <div
        v-for="(note, idx) in t.footnotes"
        :key="idx"
      >
        {{ idx + 1 }}. {{ note }}
      </div>
    </div>

    <div class="ecmt-qr">
      <vue-qrcode
        :width="96"
        :value="verifyUrl"
      />
    </div>
  </div>
</template>

<script>
import VueQrcode from 'vue-qrcode'
import { getItfRecordId } from '@/utils/itfRecordId'

const TEXTS = {
  en: {
    certificateNoLabel: 'Certificate No:',
    title: 'ECMT Certificate of Roadworthiness Test for Motor Vehicles and Trailers¹',
    registrationNumber: 'Registration Number:',
    complianceNumber: 'Certificate of Compliance Number:',
    vehicleTypeMake: 'Vehicle Type and Make:²',
    vin: 'Vehicle Identification Number (VIN):',
    engineTypeNumber: 'Engine Type / Number:³',
    the: 'The',
    legalIntro: 'Body or Establishment designated and directly supervised by the State of Registration for the purpose of UN Agreement of 1997, or of the UN Consolidated Resolution R.E.1 (TRANS/SC.1/294/Rev.5) as amended in 2001 (TRANS/WP.1/2001/25) or as subsequently amended, or of Directive 2014/45/EU or as subsequently amended,',
    legalConfirm: 'hereby confirms that the said vehicle is in compliance with the provisions of the texts above, including at least the following items to be compulsory checked:',
    place: 'Place',
    date: 'Date',
    signature: 'Signature and stamp⁵',
    footnotes: [
      'Semi-trailers included.',
      'Trailer type, if trailer.',
      'Not applicable to trailer.',
      'Including, according to UN regulation No. 24.03 and UN regulation No. 49.03 or as subsequently amended.',
      'Certificate may be filled in, stamped and signed manually or electronically.',
      'The Certificate is valid 12 months after the date of the test, and at the latest before the end of this same month.',
    ],
  },
  hy: {
    certificateNoLabel: 'Վկայական №',
    title: 'ՏՆԵԿ ավտոտրանսպորտային միջոցների և կցորդների ճանապարհային պիտանելիության փորձարկման վկայական¹',
    registrationNumber: 'Հաշվառման համարանիշ՝',
    complianceNumber: 'Համապատասխանության վկայականի համար՝',
    vehicleTypeMake: 'Տրանսպորտային միջոցի տիպ և մակնիշ՝²',
    vin: 'Նույնականացման համար (VIN)՝',
    engineTypeNumber: 'Շարժիչի տեսակ / համար՝³',
    the: 'Սույն',
    legalIntro: 'Հաշվառման պետության կողմից նշանակված և անմիջականորեն վերահսկվող մարմինը կամ կազմակերպությունը՝ 1997 թ. ՄԱԿ-ի համաձայնագրի, կամ ՄԱԿ-ի համախմբված բանաձևի R.E.1 (TRANS/SC.1/294/Rev.5)՝ փոփոխված 2001 թ. (TRANS/WP.1/2001/25) կամ հետագա փոփոխություններով, կամ 2014/45/EU հրահանգի կամ հետագա փոփոխությունների նպատակների համար,',
    legalConfirm: 'սույնով հաստատում է, որ նշված տրանսպորտային միջոցը համապատասխանում է վերոնշյալ տեքստերի դրույթներին, ներառյալ առնվազն հետևյալ պարտադիր ստուգման ենթակա կետերը՝',
    place: 'Վայր',
    date: 'Ամսաթիվ',
    signature: 'Ստորագրություն և կնիք⁵',
    footnotes: [
      'Ներառյալ կիսակցորդները։',
      'Կցորդի տիպը, եթե կցորդ է։',
      'Կիրառելի չէ կցորդի համար։',
      'Ներառյալ՝ համաձայն ՄԱԿ-ի № 24.03 և № 49.03 կանոնակարգերի կամ հետագա փոփոխությունների։',
      'Վկայականը կարող է լրացվել, կնքվել և ստորագրվել ձեռքով կամ էլեկտրոնային եղանակով։',
      'Վկայականը գործում է փորձարկման ամսաթվից 12 ամիս՝ առավելագույնը մինչև նույն ամսվա վերջ։',
    ],
  },
}

export default {
  name: 'ItfPrintContent',
  components: {
    VueQrcode,
  },
  props: {
    invoiceData: {
      type: Object,
      required: true,
    },
    lang: {
      type: String,
      default: 'en',
    },
    classificators: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    t() {
      return TEXTS[this.lang] || TEXTS.en
    },
    isHy() {
      return this.lang === 'hy'
    },
    isTrailerGroup() {
      return (this.invoiceData.car_group || '') === 'Կցորդներ'
    },
    certificateNoDisplay() {
      const no = this.invoiceData.certificateNo
      return no != null ? String(no) : '…………'
    },
    vehicleTypeMake() {
      return [this.invoiceData.car, this.invoiceData.car_model].filter(Boolean).join(' ')
    },
    engineTypeName() {
      const t = this.invoiceData.engine_type_id
      if (t && typeof t === 'object') {
        if (this.isHy) return t.name || t.nameEn || ''
        return t.nameEn || t.name || ''
      }
      return this.invoiceData.engine_type || ''
    },
    engineTypeNumber() {
      const type = this.engineTypeName
      const no = this.invoiceData.EngineNo || ''
      if (type && no) return `${type} / ${no}`
      return type || no || ''
    },
    company() {
      const c = this.invoiceData.company
      return c && typeof c === 'object' ? c : null
    },
    companyName() {
      if (!this.company) return ''
      if (!this.isHy) {
        return this.company.enName || this.company.name || ''
      }
      return this.company.name || ''
    },
    companyAddress() {
      if (!this.company) return ''
      if (!this.isHy) {
        return this.company.enAddress || ''
      }
      return [
        this.company.region,
        this.company.county,
        this.company.street,
        this.company.address,
      ].filter(Boolean).join(', ')
    },
    place() {
      if (!this.company) return ''
      if (!this.isHy) {
        return this.company.enPlace || ''
      }
      return this.company.region || ''
    },
    selectedClassificatorIds() {
      const list = this.invoiceData.classificators || []
      return list.map(c => {
        if (typeof c === 'object') return String(c.id || Reflect.get(c, '_id'))
        return String(c)
      }).filter(Boolean)
    },
    checklistItems() {
      const active = (this.classificators || [])
        .filter(c => c.status !== false)
        .filter(c => !(this.isTrailerGroup && c.hideForTrailers))
      const byParent = {}
      active.forEach(c => {
        const key = c.parent ? String(c.parent) : 'root'
        if (!byParent[key]) byParent[key] = []
        byParent[key].push(c)
      })

      Object.keys(byParent).forEach(key => {
        byParent[key].sort((a, b) => (a.ordering || 0) - (b.ordering || 0))
      })

      const roots = byParent.root || []
      const result = []

      roots.forEach(root => {
        result.push(this.mapChecklistItem(root, false))
        const children = byParent[String(root.id)] || []
        children.forEach(child => {
          result.push(this.mapChecklistItem(child, true))
        })
      })

      return result
    },
    testDate() {
      if (!this.invoiceData.createdAt) return ''
      return this.formatDate(this.invoiceData.createdAt)
    },
    nextTestDate() {
      if (!this.invoiceData.createdAt) return ''
      const date = new Date(this.invoiceData.createdAt)
      const next = Number(this.invoiceData.NextCheckup)
      if (next === 0) {
        date.setMonth(date.getMonth() + 6)
      } else if (next === 2) {
        date.setFullYear(date.getFullYear() + 2)
      } else {
        date.setFullYear(date.getFullYear() + 1)
      }
      return this.formatDate(date)
    },
    verifyUrl() {
      const id = getItfRecordId(this.invoiceData)
      if (!id) return ''
      const base = (process.env.VUE_APP_FRONTEND_URL || window.location.origin).replace(/\/$/, '')
      return `${base}/itf/verify/${id}?lang=${this.lang}`
    },
  },
  methods: {
    mapChecklistItem(c, isChild) {
      const id = String(c.id || Reflect.get(c, '_id'))
      const label = this.isHy
        ? (c.name || c.nameEn || '')
        : (c.nameEn || c.name || '')
      return {
        id,
        label,
        note: c.note || null,
        isChild,
        checked: !this.selectedClassificatorIds.includes(id),
      }
    },
    formatDate(value) {
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}.${month}.${year}`
    },
  },
}
</script>

<style lang="scss">
.ecmt-certificate {
  width: 100%;
  max-width: 210mm;
  min-height: 260mm;
  margin: 0 auto;
  padding: 10mm 14mm;
  box-sizing: border-box;
  position: relative;
  background: #fff;
  color: #000 !important;
  font-family: "Times New Roman", Times, serif !important;
  font-size: 11pt;
  font-weight: 400;
  line-height: 1.2;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  *,
  *::before,
  *::after {
    font-family: inherit !important;
    color: #000 !important;
  }
}

.ecmt-cert-no {
  text-align: right;
  font-weight: 700;
  font-size: 11pt;
  margin-bottom: 2mm;
}

.ecmt-title {
  border: 1.5pt solid #000 !important;
  box-shadow: inset 0 0 0 0.25pt #000;
  text-align: center;
  font-weight: 700;
  font-size: 11pt;
  padding: 1mm 2mm;
  margin-bottom: 5mm;
}

.ecmt-info-table {
  width: 100% !important;
  border-collapse: collapse !important;
  border-spacing: 0 !important;
  border: 1.25pt solid #000 !important;
  box-shadow: inset 0 0 0 0.25pt #000;
  margin-bottom: 0;
  table-layout: fixed;
  display: table !important;
  font-size: 11pt;

  .ecmt-info-label-col,
  .ecmt-info-value-col {
    width: 50%;
  }

  tbody {
    display: table-row-group !important;
  }

  tr {
    display: table-row !important;
  }

  td {
    display: table-cell !important;
    border-top: 1pt solid #000 !important;
    border-right: 0 !important;
    border-bottom: 1pt solid #000 !important;
    border-left: 0 !important;
    padding: 1.35mm 1.5mm !important;
    vertical-align: middle !important;
    color: #000 !important;
    background: #fff !important;
    font-size: 11pt;
  }

  .label {
    width: 50%;
    border-right: 0 !important;
    font-weight: 400;
  }

  .value {
    width: 50%;
    border-left: 0 !important;
    font-weight: 700;
    text-align: left;
    word-break: break-word;
  }
}

.ecmt-the {
  margin-top: 5mm;
  font-size: 11pt;
}

.ecmt-company-block {
  text-align: center;
  margin: 8mm 0 7mm;
}

.ecmt-company-name {
  font-weight: 700;
  font-size: 11pt;
  text-transform: uppercase;
}

.ecmt-company-address {
  margin-top: 1mm;
  font-size: 11pt;
  font-weight: 400;
}

.ecmt-legal,
.ecmt-legal-confirm {
  margin: 0 0 5mm;
  text-align: justify;
  font-size: 11pt;
  font-weight: 400;
  line-height: 1.25;
}

.ecmt-checklist {
  list-style: none;
  padding: 0;
  margin: 1mm 0 0;

  li {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1mm;
    font-size: 11pt;
    font-weight: 400;

    &.is-child {
      margin-left: 24px;
    }
  }
}

.ecmt-checkbox {
  flex: 0 0 3.5mm;
  width: 3.5mm;
  height: 3.5mm;
  border: 0.8pt solid #000 !important;
  margin-right: 2.5mm;
  margin-top: 0.35mm;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9pt;
  font-weight: 700;
  line-height: 1;
  background: #fff !important;
}

.ecmt-check-label {
  flex: 1;
  font-size: 11pt;

  sup {
    font-family: "Times New Roman", Times, serif !important;
    font-size: 7pt;
    line-height: 0;
    margin-left: 1px;
  }
}

.ecmt-sign-table {
  width: 100% !important;
  border-collapse: collapse !important;
  border-spacing: 0 !important;
  margin: 9mm 0 0;
  table-layout: fixed;
  display: table !important;
  font-size: 11pt;

  tbody {
    display: table-row-group !important;
  }

  tr {
    display: table-row !important;
  }

  td {
    display: table-cell !important;
    width: 33.33%;
    border: none !important;
    text-align: center;
    vertical-align: bottom;
    padding: 0 4px;
    color: #000 !important;
    background: transparent !important;
    font-size: 11pt;
  }

  .ecmt-sign-values-row td {
    font-weight: 700;
    font-size: 11pt;
    padding-bottom: 1.5mm;
    min-height: 5mm;
  }

  .ecmt-sign-line-row td {
    padding: 0;
  }

  .ecmt-sign-labels-row td {
    font-size: 11pt;
    font-weight: 400;
    padding-top: 1.5mm;

    sup {
      font-family: "Times New Roman", Times, serif !important;
      font-size: 7pt;
    }
  }
}

.ecmt-sign-full-line {
  width: 100%;
  border-top: 1px solid #000 !important;
  height: 0;
  margin: 0;
}

.ecmt-next-note {
  margin: 9mm 0 0;
  font-size: 11pt;
  font-weight: 400;

  .ecmt-note-lead {
    font-weight: 700;
    margin-right: 0.25em;
  }

  sup {
    font-family: "Times New Roman", Times, serif !important;
    font-size: 7pt;
  }

  .ecmt-next-date {
    margin-left: 6px;
    font-weight: 700;
    text-decoration: underline;
    padding: 0 4px;
  }
}

.ecmt-footnotes-line {
  width: 50mm;
  height: 0;
  margin: 12mm 0 0;
  border: 0;
  border-top: 0.75pt solid #000;
}

.ecmt-footnotes {
  margin-top: 2mm;
  font-size: 8pt;
  font-weight: 400;
  line-height: 1.2;
  color: #000 !important;
  max-width: calc(100% - 34mm);
}

.ecmt-qr {
  position: absolute;
  right: 14mm;
  bottom: 10mm;
  width: 96px;
  height: 96px;
  line-height: 0;

  img,
  canvas,
  svg {
    display: block;
    width: 96px !important;
    height: 96px !important;
  }
}

.ecmt-title,
.ecmt-cert-no,
.ecmt-legal,
.ecmt-legal-confirm,
.ecmt-check-label,
.ecmt-next-note,
.ecmt-footnotes {
  sup {
    font-family: "Times New Roman", Times, serif !important;
    font-size: 7pt;
  }
}

@media print {
  html.itf-printing .ecmt-certificate {
    width: 210mm !important;
    max-width: 210mm !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 auto !important;
    padding: 12mm 15mm 10mm !important;
    overflow: visible !important;
    box-sizing: border-box !important;
    box-shadow: none !important;
    font-family: "Times New Roman", Times, serif !important;
    font-size: 11pt !important;
    color: #000 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    *,
    *::before,
    *::after {
      font-family: "Times New Roman", Times, serif !important;
      color: #000 !important;
    }
  }

  html.itf-printing {
    .ecmt-cert-no,
    .ecmt-title,
    .ecmt-the,
    .ecmt-company-name,
    .ecmt-company-address,
    .ecmt-legal,
    .ecmt-legal-confirm,
    .ecmt-checklist li,
    .ecmt-check-label,
    .ecmt-next-note {
      font-size: 11pt !important;
    }

    .ecmt-info-table,
    .ecmt-info-table td,
    .ecmt-sign-table,
    .ecmt-sign-table td,
    .ecmt-sign-values-row td,
    .ecmt-sign-labels-row td {
      font-size: 11pt !important;
    }

    .ecmt-footnotes {
      font-size: 8pt !important;
    }

    .ecmt-title {
      border: 1.5pt solid #000 !important;
      box-shadow: inset 0 0 0 0.25pt #000 !important;
    }

    .ecmt-info-table {
      display: table !important;
      width: 100% !important;
      border-collapse: collapse !important;
      border: 1.25pt solid #000 !important;
      box-shadow: inset 0 0 0 0.25pt #000 !important;

      tr {
        display: table-row !important;
        page-break-inside: avoid;
      }

      td {
        display: table-cell !important;
        border-top: 1pt solid #000 !important;
        border-right: 0 !important;
        border-bottom: 1pt solid #000 !important;
        border-left: 0 !important;
      }

      .label {
        border-right: 0 !important;
      }

      .value {
        border-left: 0 !important;
      }
    }

    .ecmt-sign-table {
      display: table !important;
      width: 100% !important;

      tr {
        display: table-row !important;
      }

      td {
        display: table-cell !important;
      }
    }

    .ecmt-sign-full-line {
      border-top: 1px solid #000 !important;
    }

    .ecmt-checkbox {
      border: 0.8pt solid #000 !important;
    }

    .ecmt-footnotes-line {
      display: block !important;
      width: 50mm !important;
      height: 0 !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      border: 0 !important;
      border-top: 0.75pt solid #000 !important;
    }

    .ecmt-qr {
      display: block !important;
      position: absolute !important;
      right: 14mm !important;
      bottom: 10mm !important;
    }
  }
}
</style>
